import React, { useState } from "react";
import { Card, Avatar, Spinner, Button } from "flowbite-react";
import { Calendar, Clock, Users, User, Video, CheckCircle, XCircle, Sparkles, ArrowRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { NoCreditModal, ConfirmParticipationModal } from "./CreditModals";

export interface PublicAppointment {
  id: string;
  therapistName: string;
  therapistId?: string;
  patientName?: string;
  patientId?: string;
  description?: string;
  date: Date;
  duration: number;
  status: "scheduled" | "in-progress" | "completed";
  roomId?: string;
  public: boolean;
  expectedParticipants?: number;
}

interface PublicSessionsListProps {
  appointments: PublicAppointment[];
  isLoading?: boolean;
  userParticipations?: Set<string>;
  schedulingAppointment?: string | null;
  schedulingError?: { [key: string]: string };
  schedulingSuccess?: { [key: string]: boolean };
  onScheduleParticipation?: (appointmentId: string) => void;
  emptyMessage?: string;
  currentUserId?: string;
  participationCredits?: number;
  onViewPlans?: () => void;
}

const PublicSessionsList: React.FC<PublicSessionsListProps> = ({
  appointments,
  isLoading = false,
  userParticipations = new Set(),
  schedulingAppointment = null,
  schedulingError = {},
  schedulingSuccess = {},
  onScheduleParticipation,
  emptyMessage = "Nenhuma sessão futura disponível no momento.",
  currentUserId,
  participationCredits = 0,
  onViewPlans,
}) => {
  const [showNoCreditModal, setShowNoCreditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ""}`;
    }
    return `${mins}min`;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 min-h-[400px] relative z-10">
        <Spinner aria-label="Carregando sessões..." size="xl" />
        <p className="mt-4 text-gray-600 text-lg">Carregando...</p>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card className="p-8 text-center bg-white/60 backdrop-blur-sm border-dashed border-2 border-[#3A5859]/30">
        <div className="flex flex-col items-center space-y-4">
          <div className="p-4 bg-gradient-to-br from-[#3A5859]/20 to-[#A56096]/20 rounded-full">
            <Calendar className="w-8 h-8 text-[#3A5859]" />
          </div>
          <p className="text-gray-500">{emptyMessage}</p>
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-[#A56096]/60 animate-pulse" />
            <span className="text-sm text-gray-400">Novas sessões serão agendadas em breve</span>
            <Sparkles className="w-4 h-4 text-[#A56096]/60 animate-pulse delay-300" />
          </div>
        </div>
      </Card>
    );
  }

  const handleScheduleClick = (appointmentId: string) => {
    if (participationCredits > 0) {
      setSelectedAppointmentId(appointmentId);
      setShowConfirmModal(true);
    } else {
      setShowNoCreditModal(true);
    }
  };

  const confirmScheduleParticipation = () => {
    if (selectedAppointmentId) {
      onScheduleParticipation?.(selectedAppointmentId);
      setShowConfirmModal(false);
      setSelectedAppointmentId(null);
    }
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {appointments.map((appointment) => (
        <Card
          key={appointment.id}
          className="hover:shadow-xl hover:scale-105 transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:bg-white/90">
          <div className="p-6 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Star className="w-4 h-4 text-[#3A5859]/30 animate-pulse" />
            </div>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <Avatar
                  placeholderInitials={appointment.therapistName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)}
                  rounded
                  size="sm"
                  className="flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-[#3A5859] truncate">{appointment.therapistName}</h4>
                </div>
              </div>
            </div>
            {appointment.description && <p className="text-gray-700 text-sm mb-4">{appointment.description}</p>}
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-2" />
                {formatDate(appointment.date)}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Users className="w-4 h-4 mr-2" />
                Duração: {formatDuration(appointment.duration)}
              </div>
              {appointment.expectedParticipants !== undefined && (
                <div className="flex items-center text-sm text-gray-600">
                  <User className="w-4 h-4 mr-2" />
                  {appointment.expectedParticipants} participante{appointment.expectedParticipants !== 1 ? "s" : ""} previsto
                  {appointment.expectedParticipants !== 1 ? "s" : ""}
                </div>
              )}
            </div>
            <div className="space-y-3">
              {appointment.roomId && appointment.status === "in-progress" && (
                <Link to={`/sala/${appointment.roomId}`} className="w-full">
                  <Button className="w-full bg-gradient-to-r from-[#A56096] to-[#8B4A7A] hover:from-[#8B4A7A] hover:to-[#7A4169] transform hover:scale-[1.02] transition-all duration-200 shadow-lg">
                    <Video className="w-4 h-4 mr-2" />
                    Entrar na Consulta
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}

              {onScheduleParticipation && (
                <>
                  {userParticipations.has(appointment.id) || 
                   (currentUserId && (appointment.therapistId === currentUserId || appointment.patientId === currentUserId)) ? (
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white" disabled>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Participação Agendada
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-gradient-to-r from-[#3A5859] to-[#2C4344] hover:from-[#2C4344] hover:to-[#1F2F30] transform hover:scale-[1.02] transition-all duration-200 shadow-lg"
                      onClick={() => handleScheduleClick(appointment.id)}
                      disabled={schedulingAppointment === appointment.id}>
                      {schedulingAppointment === appointment.id ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Agendando...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          Agendar Participação
                          <Sparkles className="w-3 h-3 ml-2 animate-pulse" />
                        </>
                      )}
                    </Button>
                  )}

                  {schedulingError[appointment.id] && (
                    <div className="flex items-center text-sm text-red-600 mt-2">
                      <XCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>{schedulingError[appointment.id]}</span>
                    </div>
                  )}

                  {schedulingSuccess[appointment.id] && (
                    <div className="flex items-center text-sm text-green-600 mt-2">
                      <CheckCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Participação agendada com sucesso!</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
      </div>

      <NoCreditModal
        isOpen={showNoCreditModal}
        onClose={() => setShowNoCreditModal(false)}
        onViewPlans={onViewPlans || (() => {})}
        type="participation"
      />

      <ConfirmParticipationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedAppointmentId(null);
        }}
        onConfirm={confirmScheduleParticipation}
        isProcessing={schedulingAppointment === selectedAppointmentId}
        participationCredits={participationCredits}
      />
    </>
  );
};

export default PublicSessionsList;
